import { CheckCircle2, Send } from 'lucide-react';

import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { useTranslation } from 'react-i18next';

interface DownloadPDFDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signatoriesLength: number;
  handleSendToSignature: () => void;
  handleDownloadPdf: () => void;
}

const DownloadPDFDialog = ({
  open,
  onOpenChange,
  signatoriesLength,
  handleSendToSignature,
  handleDownloadPdf,
}: DownloadPDFDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* w-[calc(100%-2rem)] gives the dialog ~16px gutters on both sides on
          phones (the base DialogContent is fixed left:50% translate:-50% so
          plain mx- would overflow). p-4 sm:p-6 tightens padding on phones. */}
      <DialogContent className="w-[calc(100%-2rem)] sm:w-full max-h-[90vh] z-999 flex flex-col overflow-hidden p-4 sm:p-6">
        <div className="shrink-0">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg pr-6">
              {t('GENERATEREVIEW_DIALOGS_SENDRECOMMENDEDTITLE')}
            </DialogTitle>
            <DialogDescription>{t('GENERATEREVIEW_DIALOGS_SENDRECOMMENDEDDESC')}</DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          <div className="space-y-3 text-sm text-[hsl(var(--fg))]">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-selise-blue mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-[hsl(var(--fg))]">
                  {t('GENERATEREVIEW_DIALOGS_BENEFITS_LEGALTITLE')}
                </p>
                <p className="text-globe-grey">{t('GENERATEREVIEW_DIALOGS_BENEFITS_LEGALDESC')}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-selise-blue mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-[hsl(var(--fg))]">
                  {t('GENERATEREVIEW_DIALOGS_BENEFITS_PRIVACYTITLE')}
                </p>
                <p className="text-globe-grey">
                  {t('GENERATEREVIEW_DIALOGS_BENEFITS_PRIVACYDESC')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-selise-blue mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-[hsl(var(--fg))]">
                  {t('GENERATEREVIEW_DIALOGS_BENEFITS_VERSIONTITLE')}
                </p>
                <p className="text-globe-grey">
                  {t('GENERATEREVIEW_DIALOGS_BENEFITS_VERSIONDESC')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 pt-3 sm:pt-4 border-t border-border gap-2 sm:gap-0 sm:flex-wrap sm:justify-start">
          <Button
            onClick={handleSendToSignature}
            className="bg-selise-blue hover:bg-oxford-blue text-white hover:text-white w-full sm:w-auto sm:flex-1 sm:min-w-0 max-w-full whitespace-normal"
            disabled={signatoriesLength === 0}
          >
            <Send className="w-4 h-4 mr-2" />
            {t('GENERATEREVIEW_DIALOGS_SENDWITHSELISE')}
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            className="w-full sm:w-auto sm:flex-1 sm:min-w-0 max-w-full whitespace-normal text-globe-grey"
          >
            {t('GENERATEREVIEW_DIALOGS_DOWNLOADANYWAY')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadPDFDialog;
